import React from "react";

const WhyYouLoveIt = () => {
  const loveFeatures = [
    {
      title: "Create Fully Working Forms Instantly",
      desc: "Use the Smart Form Generator to build anything from a yoga studio feedback form to a complex project inquiry. In seconds, get accessible, SEO-friendly forms ready for production.",
      icon: "bolt",
    },
    {
      title: "Works with Any Platform",
      desc: "Perfect for Indian creators using Netlify, Vercel, or GitHub Pages. Whether it's a Next.js app or a simple portfolio, just paste and deploy.",
      icon: "dynamic_feed",
    },
    {
      title: "Secure & Spam-Free by Default",
      desc: "Every endpoint is pre-secured with smart spam filtering, HTTPS encryption, and rate limiting. Enterprise-grade security for everyday builders.",
      icon: "verified_user",
    },
    {
      title: "Effortless CRM Integration",
      desc: "Sync your submissions with Zapier, HubSpot, or Notion. Trigger auto-replies or update your CRM without writing a single line of backend logic.",
      icon: "cable",
    },
  ];

  return (
    <section id="why-love-it" className="py-16 sm:py-20 lg:py-24 bg-white overflow-hidden">
      <div className="mx-auto w-[95%] md:w-[90%]">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
          {/* VISUAL */}
          <div className="w-full lg:w-1/2 relative opacity-0 animate-fade-in-up pb-12 sm:pb-10">
            <div className="relative rounded-3xl bg-[#0B1C2D] p-1 shadow-2xl overflow-hidden border border-white/10">
              <div className="bg-[#1a2f46] px-4 sm:px-5 py-4 flex items-center justify-between border-b border-white/10 gap-2">
                <div className="flex gap-1.5 shrink-0">
                  <div className="size-2.5 rounded-full bg-[#E63946]"></div>
                  <div className="size-2.5 rounded-full bg-slate-500/50"></div>
                  <div className="size-2.5 rounded-full bg-slate-500/50"></div>
                </div>
                <div className="text-[9px] sm:text-[10px] text-white/40 font-mono tracking-widest uppercase truncate">
                  Live Workspace
                </div>
                <div className="flex gap-2 shrink-0">
                  <div className="h-4 w-10 sm:w-12 rounded-full bg-white/5"></div>
                </div>
              </div>

              <div className="p-4 sm:p-8 bg-[#0B1C2D]">
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-[10px] font-black text-[#E63946] uppercase mb-1">
                    Form Brief
                  </p>
                  <p className="text-xs sm:text-sm text-white font-medium leading-relaxed italic">
                    "Feedback form for my{" "}
                    <span className="text-[#E63946]">
                      Yoga Studio in Bengaluru
                    </span>
                    — name, rating, and message."
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-1 flex-1 bg-gradient-to-r from-[#E63946] to-transparent rounded"></div>
                    <span className="text-[10px] text-white/30 font-mono whitespace-nowrap">
                      FORM READY
                    </span>
                  </div>

                  <div className="bg-[#0f172a] rounded-lg p-3 sm:p-4 font-mono text-[10px] sm:text-[11px] text-blue-300 space-y-1 overflow-x-auto">
                    <p className="whitespace-nowrap">
                      <span className="text-pink-400">{"<form "}</span>
                      {"action="}
                      <span className="text-green-400">
                        "https://contacthq.io/f/.."
                      </span>
                      {">"}
                    </p>
                    <p className="pl-4 whitespace-nowrap">
                      {"<input "}
                      <span className="text-yellow-400">type</span>
                      {"="}
                      <span className="text-green-400">"text"</span>
                      {" name="}
                      <span className="text-green-400">"name"</span>
                      {" />"}
                    </p>
                    <p className="pl-4 whitespace-nowrap">
                      {"<button "}
                      <span className="text-yellow-400">type</span>
                      {"="}
                      <span className="text-green-400">"submit"</span>
                      {">"}
                    </p>
                    <p className="text-pink-400">{"</form>"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 right-2 sm:right-0 bg-white border border-[#E2E8F0] p-3 sm:p-4 rounded-2xl shadow-xl flex flex-col gap-2 animate-float">
              <p className="text-[10px] font-black text-[#64748B] uppercase">
                Syncs With
              </p>
              <div className="flex gap-3">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png"
                  alt="Notion"
                  className="size-5 grayscale hover:grayscale-0 transition-all"
                />
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg"
                  alt="Slack"
                  className="size-5 grayscale hover:grayscale-0 transition-all"
                />
                <img
                  src="https://static-00.iconduck.com/assets.00/vercel-icon-512x449-3422ba38.png"
                  alt="Vercel"
                  className="size-5 grayscale hover:grayscale-0 transition-all"
                />
              </div>
            </div>
          </div>

          {/* TEXT */}
          <div className="w-full lg:w-1/2">
            <h2 className="text-sm sm:text-base font-bold tracking-[0.2em] text-[#E63946] uppercase mb-3 sm:mb-4">
              Why You’ll Love It
            </h2>
            <h3 className="text-2xl sm:text-4xl font-bold tracking-tight text-[#0B1C2D] lg:text-5xl leading-tight mb-5 sm:mb-6">
              Your Personal Assistant for{" "}
              <br className="hidden sm:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E63946] to-[#0B1C2D]">
                Everything Form-Related.
              </span>
            </h3>
            <p className="text-base sm:text-lg text-[#64748B] mb-8 sm:mb-12">
              ContactHQ takes away the busywork of debugging backend logic so
              you can focus on building, not fixing.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 sm:gap-y-10 gap-x-8">
              {loveFeatures.map((f, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-3 group opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${i * 150}ms` }}
                >
                  <div className="size-10 rounded-lg bg-slate-50 text-[#0B1C2D] flex items-center justify-center border border-[#E2E8F0] group-hover:bg-[#E63946] group-hover:text-white transition-all duration-300">
                    <span className="material-symbols-outlined text-xl">
                      {f.icon}
                    </span>
                  </div>
                  <h4 className="font-bold text-[#0B1C2D] text-sm sm:text-base">
                    {f.title}
                  </h4>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 sm:mt-24 relative rounded-3xl sm:rounded-[2.5rem] bg-slate-50 border border-[#E2E8F0] p-6 sm:p-8 md:p-12 overflow-hidden opacity-0 animate-fade-in-up [animation-delay:600ms]">
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 sm:gap-10">
            <div className="flex-1">
              <h4 className="text-xl sm:text-2xl font-bold text-[#0B1C2D] mb-3 sm:mb-4">
                Imagine This: Your Business, Simplified.
              </h4>
              <p className="text-base sm:text-lg text-[#64748B] leading-relaxed">
                "You’re building a landing page for your{" "}
                <span className="text-[#0B1C2D] font-bold">
                  Bakery in Kochi
                </span>
                . You pick a template, customize fields, paste the embed, and
                hit publish. The form is live — secure, responsive, and
                connected to your inbox. No servers, no debugging. It just
                works."
              </p>
            </div>
            <div className="flex-none flex items-center justify-center p-6 bg-white rounded-3xl shadow-lg border border-[#E2E8F0] rotate-2 hover:rotate-0 transition-transform duration-500">
              <div className="text-center">
                <div className="text-3xl font-black text-[#E63946]">10x</div>
                <div className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">
                  Faster Launch
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -top-12 -right-12 size-48 bg-[#E63946]/5 rounded-full blur-3xl"></div>
        </div>
      </div>
    </section>
  );
};

export default WhyYouLoveIt;
